/**
 * Front-Desk simple mode (#548) — the stripped, one-tap surface for
 * daily floor staff (2026-07-21 owner walkthrough, Julian, emphasized):
 * "this dashboard and all the paper… it looks a little bit too much…
 * something really simple for the daily employees… they just press
 * check-in and they don't have to see everything else." Minimal stated
 * scope: check-in, dog lookup, time clock — "Nobody really needs more
 * than that." This is the first surface Kylie (new receptionist,
 * ex-Dogtopia) touches on Mon 2026-07-27.
 *
 * What it is (and deliberately isn't):
 *  1. A huge, autofocused, search-FIRST check-in flow — type the dog's
 *     name, tap the dog, tap Check In. Reuses the EXISTING check-in
 *     action (checkInBooking → POST /api/bookings/admin/:id/check-in);
 *     no new booking state transition is invented.
 *  2. Today's expected arrivals (the SAME /today source the dashboard
 *     owns — one truth, Principle 1) with one-tap Check In per row,
 *     dog-first display (dog name big, owner secondary) consistent with
 *     #545. Built with this page's OWN lightweight row component
 *     (FrontDeskRow) — NOT the shared ArrivalsDepartures — so #545's
 *     restyle of the shared dashboard components can't file-conflict
 *     with this lane. Consistency is by token/style, not shared file.
 *  3. A clock in / out shortcut that links to the existing Time Clock
 *     surface (/timeclock) — this page never reimplements punches.
 *  3b. #716 — AFTER a boarding check-in succeeds, an optional care-capture
 *     sheet (belongings / feeding / meds, pre-filled from the dog's last
 *     stay). It opens only once the check-in POST has resolved, so it can
 *     never block the action it follows, and only when
 *     `features.checkInCapture` is on — no tenant enables it today.
 *  3c. Ted #2 (#905) — HOUSEHOLD check-in. Rows that share a `customerId`
 *     sit together under one header ("Hazel, Hank & Skipper — Jodie
 *     Royer") with a single "Check in all (3)" beside the per-row buttons.
 *     One POST per booking (the same route), all settled before ONE
 *     refetch; a refused dog is named on the banner and never stops the
 *     siblings. Grouping is by customer id only — never by name. The
 *     #716 sheet is not offered on a household check-in (see handler).
 *  3d. Ted #32c (#1158, #1160) — the Expected row carries the reservation's
 *     own actions, as Gingr's does: Edit, No-show and Cancel (the X) beside
 *     Check In, through the SAME dialogs the Bookings page uses
 *     (`useReservationActions`). And Check In on a dog whose family has other
 *     dogs still to arrive opens ONE picker (`FamilyCheckInDialog`) with only
 *     the tapped dog going in and every sibling unticked — Ted: "it should not
 *     automatically check them all in". "Check in all (N)" opens the same
 *     picker with everyone ticked, so the desk still chooses.
 *  4. The Going home LIST stays behind a UI toggle that starts OFF (Julian,
 *     2026-07-21: "I don't really want the checkout. We had to discuss.").
 *     Check Out itself is no longer hidden: Sawyer, 10-30, "I didn't see a
 *     check out button on the going home section either", so each dog who can
 *     leave has one on the "Going home today" list. Both routes end at the
 *     Bookings page's money-path orchestrator (`checkOutPath`, never a direct
 *     checkout POST — checkout is a money action, same rule as
 *     ArrivalsDepartures).
 *
 * Data honesty (Principle 5): when the server says today's zeros are
 * structural (`dataSuspect`), the empty state says so ("Today's schedule
 * isn't in this system yet") and the server's warning renders verbatim —
 * a quiet day and a broken sync must never look the same.
 *
 * Kiosk-friendly: large touch targets (≥44px, most ≥56px), a prominent
 * search surface, and iPad-portrait and desktop support.
 *
 * RBAC: gated (nav + route) on ROUTE_ACCESS_RULES['/front-desk'] =
 * bookings.daycare-boarding:write — exactly owner/manager/receptionist,
 * the roles the server admits to the check-in POST (the #484 lesson:
 * nav visibility matches the server permission the page consumes).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  checkInBooking,
  fetchServiceTypes,
  isArrivalUnverified,
  readSession,
  todayIsoDate,
  undoCheckInBooking,
  undoCheckOutBooking,
  type AdminBooking,
  type DogCard,
  type ServiceTypeSummary,
} from '../lib/api.js';
// Ted #26 (#987) — confirm a pending reservation from the desk, with the
// household's other pending reservations reviewable before a batch applies.
import { ClientNotNotifiedPill } from '../components/booking/ClientNotNotifiedPill.js';
// Sawyer 10 customer-booking-verify-policy (C5 C6) — the owner's attestations at booking.
import { OwnerCareCheckPill } from '../components/booking/OwnerCareCheckPill.js';
import { ConfirmPendingDialog } from '../components/booking/ConfirmPendingDialog.js';
import { useConfirmPending } from '../components/booking/useConfirmPending.js';
// Ted #32c (#1158) — Cancel / Edit / No-show on the Expected row.
import { ExpectedRowActions } from '../components/booking/ExpectedRowActions.js';
import { reservationTargetFromArrival } from '../components/booking/reservation-target.js';
import { useReservationActions } from '../components/booking/useReservationActions.js';
import {
  DogIdCard,
  DogStatusStrip,
  DogFlagChips,
  useArrivalVerification,
} from '../components/dog-first/index.js';
import { resolveDogFlags } from '../components/dog-first/flag-skew.js';
// Sawyer 11 client-notes-agreements (F1) — the "New client note" flag + row digest.
import {
  ClientNoteChip,
  ClientNoteSignalsProvider,
  ClientNotesRowSlot,
} from '../components/customers/client-note-signals.js';
import {
  CheckInCareSheet,
  isBoardingServiceName,
  type CheckInCareSheetDog,
} from '../components/check-in/CheckInCareSheet.js';
// Ted 09-24 / 09-29 (lane LC) — "Before you check in": the registry's
// check-in alerts, acknowledged one by one; Do Not Admit as a stop; the heat
// question for the dogs the rule applies to. One component, every surface.
import { useCheckInInterrupt } from '../components/check-in/useCheckInInterrupt.js';
import { useRunCardPrompt } from '../components/run-cards/useRunCardPrompt.js';
import {
  describeStopped,
  planCheckInInterrupt,
  type InterruptDecision,
} from '../components/check-in/check-in-interrupt.js';
// Ted #32c (#1160) — the one family picker, siblings unticked by default.
import { FamilyCheckInDialog } from '../components/check-in/FamilyCheckInDialog.js';
import {
  familyMemberFromArrival,
  siblingsStillToArrive,
} from '../components/check-in/family-check-in.js';
import { ArrivalsBoard } from '../components/arrivals-board/index.js';
import { checkOutPath } from '../components/checkout/check-out-path.js';
// Sawyer answers 10-29, item 2 — "Going home today" (belongings, locker, food), on screen and printable.
import { GoingHomeBoard, GoingHomeToday } from '../components/going-home/index.js';
// Sawyer 03 today-notes-board (C2, G8) — the visit's notes on the row.
import {
  BookingNoteCallout,
  DeskNoteDialog,
  factsFromArrival,
  factsFromPending,
  useOwnerNotesPrompt,
  type OwnerNotesPrompt,
} from '../components/booking-notes/index.js';
import { AddServiceDialog } from '../components/booking/AddServiceDialog.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useBookingRefresh } from '../contexts/BookingRefreshContext.js';
import {
  fetchDashboardDataStatus,
  fetchDashboardToday,
  formatTime,
  isScheduleDataSuspect,
  type DashboardArrival,
  type OutstandingCheckout,
  // Sawyer 03 today-notes-board (G8)
  deskNotesOf,
} from '../components/dashboard/dashboard-api.js';
import { useDashboardResource } from '../components/dashboard/useDashboardResource.js';
import {
  checkInHousehold,
  householdDogNames,
  joinDogNames,
  segmentByHousehold,
  summarizeHouseholdCheckIn,
} from '../components/dashboard/household-checkin.js';

/** Lane LC — what a re-run of `handleCheckIn` already knows from the "Before you check in" step. */
interface CheckInOpts {
  heatConfirmed?: boolean;
  /** True ⇒ the step has been shown (or was not needed); do not ask again. */
  interrupted?: boolean;
}
import { countVisits } from '../components/dashboard/service-legs.js';
import { Icon } from '../components/icons.js';
import { OwnerLink } from '../components/owner-link/index.js';
import './FrontDesk.css';

type FrontDeskProps = {
  config: ClientConfig;
};

/**
 * Rank a row against the search query. 2 = a dog name matched, 1 = the
 * owner name matched, 0 = no match. Mirrors the #547 pet-name bias: dog
 * matches lead. The check-in surface is keyed on the DOG's name.
 */
function matchRank(item: DashboardArrival, q: string): 0 | 1 | 2 {
  if (q.length === 0) return 1;
  const needle = q.toLowerCase();
  const dogHit = item.dogNames.some((n) => n.toLowerCase().includes(needle));
  if (dogHit) return 2;
  if (item.customerName.toLowerCase().includes(needle)) return 1;
  return 0;
}

export function FrontDesk({ config }: FrontDeskProps): JSX.Element {
  const { token } = readSession();
  const { isValidatingSession } = useAuth();
  const apiUrl = config.deployment.apiUrl;
  const navigate = useNavigate();
  // Ted #23 (#984) — the arrival whose visit is getting a service added.
  const [addServiceFor, setAddServiceFor] = useState<DashboardArrival | null>(null);
  // Sawyer 03 today-notes-board (G8) — the row whose desk note is open.
  const [deskNoteFor, setDeskNoteFor] = useState<DashboardArrival | null>(null);
  const date = todayIsoDate();

  const [query, setQuery] = useState('');
  // Ted #2 (#905) — a Set, not a single id: a household check-in puts every
  // row of the group into its loading state at once.
  const [actionLoading, setActionLoading] = useState<Set<string>>(() => new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [checkedIn, setCheckedIn] = useState<Set<string>>(() => new Set());
  // OPEN QUESTION (2026-07-21, Julian: "I don't really want the checkout.
  // We had to discuss."). Checkout is OFF by default; staff opt in.
  const [showCheckout, setShowCheckout] = useState(false);
  // #564 — the printable morning board overlay (open/closed).
  const [boardOpen, setBoardOpen] = useState(false);
  const [goingHomeOpen, setGoingHomeOpen] = useState(false);
  // #581-followup — the pet ID card opened by clicking a dog's name. Tracks
  // id + name so the modal header shows the right name before the fetch lands.
  const [openDog, setOpenDog] = useState<{ id: string; name: string } | null>(
    null,
  );
  const openDogCard = useCallback(
    (id: string, name: string) => setOpenDog({ id, name }),
    [],
  );
  // #716 — the booking whose check-in capture sheet is open, or null. Set
  // only AFTER the check-in POST resolves; see handleCheckIn.
  const [captureFor, setCaptureFor] = useState<{
    bookingId: string;
    dogs: CheckInCareSheetDog[];
  } | null>(null);
  // Dark launch (#716): no client config sets this today, so tapping Check
  // In behaves exactly as it did before this shipped.
  const captureEnabled = config.features?.checkInCapture === true;
  // Ted Item 34 — offer the run card the moment a check-in succeeds, and a
  // "Print run cards" button here so the desk never goes to Bookings for it.
  const runCards = useRunCardPrompt({
    apiUrl,
    token,
    enabled: config.features?.runCardPromptOnCheckIn !== false,
  });
  const offerRunCards = runCards.offer;

  // Ted #22 (#983) — the Outstanding row whose reason field is open, and
  // what has been typed into it. Inline rather than a modal: the desk is
  // standing up with a leash in one hand, and the reason is one short line.
  const [reopening, setReopening] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const { version: bookingRefreshVersion } = useBookingRefresh();
  const refreshKey = localRefreshKey + bookingRefreshVersion;
  const bumpRefresh = useCallback(() => setLocalRefreshKey((k) => k + 1), []);

  const fetchesEnabled = Boolean(token) && !isValidatingSession;

  // Ted #26 (#987) — the review dialog names the SERVICE, and `/today` only
  // carries the engine key ("daycare"). One catalog read gives it the
  // customer-facing name; failure leaves a neutral word, never a raw key
  // (DESIGN-PHILOSOPHY anti-patterns).
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeSummary[]>([]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void fetchServiceTypes(apiUrl, token)
      .then((list) => {
        if (!cancelled) setServiceTypes(list);
      })
      .catch(() => {
        /* non-fatal — the dialog says "Reservation" */
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token]);
  const serviceLabelForBooking = useCallback(
    (b: AdminBooking): string =>
      serviceTypes.find((s) => s.id === b.service_type_id)?.display_name ??
      'Reservation',
    [serviceTypes],
  );
  // Sawyer #1296 — "Add this to the owner's notes?" after an accept. The
  // prompt hook is called further down (its `blocked` reads dialogs declared
  // there), so the accept reaches it through this ref.
  const offerOwnerNotesRef = useRef<OwnerNotesPrompt['offer']>(() => undefined);
  const confirmFlow = useConfirmPending({
    apiUrl,
    serviceLabelForBooking,
    onApplied: bumpRefresh,
    onConfirmed: (items) =>
      offerOwnerNotesRef.current(
        items.map((i) =>
          factsFromPending(i, arrivalsRef.current.find((a) => a.bookingId === i.bookingId)?.customerName ?? null),
        ),
      ),
  });
  const beginConfirm = useCallback(
    (item: DashboardArrival): void => {
      void confirmFlow.begin({
        bookingId: item.bookingId,
        customerId: item.customerId ?? '',
        customerName: item.customerName,
        // Only used if the wider lookup cannot return the row: `/today` has
        // no notes or price, and inventing either would be a lie (Principle
        // 5). The real list comes from /schedule.
        fallback: {
          bookingId: item.bookingId,
          customerId: item.customerId ?? '',
          dogNames: item.dogNames,
          serviceLabel: item.serviceDisplayName ?? 'Reservation',
          dateISO: `${date}T00:00:00.000Z`,
          timeLabel: item.expectedTime ? formatTime(item.expectedTime) : null,
          // Sawyer 03 today-notes-board (C2) — `/today` carries the booking
          // note now; an older server omits it (null, as before).
          notes: item.bookingNote ?? null,
          totalCents: 0,
          addOns: [],
          // Sawyer #1296 — the same server fact /schedule would have carried.
          bookingNoteInOwnerNotes: item.bookingNoteInOwnerNotes ?? null,
        },
      });
    },
    [confirmFlow, date],
  );

  // Ted #32c (#1158) — Cancel / Edit / No-show on the Expected row. The
  // catalog is already loaded above, so the Edit dialog needs no second read.
  const reservation = useReservationActions({
    apiUrl,
    onChanged: bumpRefresh,
    serviceTypes,
  });
  // Ted #32c (#1160) — the family picker. `anchor` is the dog whose Check In
  // was tapped (null for "Check in all"); `siblings` are the others still to
  // arrive. Nothing here posts: it only decides which ids the existing
  // check-in paths are handed.
  const [family, setFamily] = useState<{
    anchor: DashboardArrival | null;
    siblings: DashboardArrival[];
    startTicked: 'anchor-only' | 'all';
  } | null>(null);

  const dataStatus = useDashboardResource(
    () => fetchDashboardDataStatus(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  const today = useDashboardResource(
    () => fetchDashboardToday(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );

  const scheduleSuspect =
    dataStatus.data !== null && isScheduleDataSuspect(dataStatus.data);

  const arrivals = today.data?.arrivals ?? [];
  const departures = today.data?.departures ?? [];
  // Ted #22 (#983) — the unsettled checkouts the day left behind. Absent on
  // a server older than #983 (frontends auto-deploy, the server does not),
  // and [] for A/R-blind roles: both render nothing, today's behaviour.
  const outstanding: OutstandingCheckout[] = today.data?.outstandingToday ?? [];

  // #716 — `handleCheckIn` needs the row it just acted on to decide whether
  // to offer the capture sheet, but must not take `arrivals` as a dep: the
  // list identity changes on every refetch and would rebuild the callback
  // (and with it every row's onClick) mid-morning.
  const arrivalsRef = useRef(arrivals);
  arrivalsRef.current = arrivals;

  // Ted #28 (#1050) — the arrival-verify step. The SERVER refuses a
  // transported dog's check-in (409 `arrival_unverified`) until a human
  // confirms it is physically here; this queue asks, one dog at a time, and
  // re-runs the check-in with the answer. `checkInRef` lets a queued answer
  // call the latest `handleCheckIn` without a dependency cycle.
  const { ask: askArrival, dialog: arrivalDialog } = useArrivalVerification();
  const checkInRef = useRef<(bookingId: string, arrivalVerified?: boolean, opts?: CheckInOpts) => Promise<void>>();
  // Lane LC — the "Before you check in" step, asked BEFORE the first POST
  // from the row's own flags; the 409 path above stays as the fallback.
  const { ask: askInterrupt, dialog: interruptDialog } = useCheckInInterrupt();
  // Sawyer #1296 — the owner's-notes question waits behind every other dialog
  // on this page (never modal-on-modal) and shows when they have closed.
  const ownerNotes = useOwnerNotesPrompt({
    apiUrl,
    onAdded: bumpRefresh,
    blocked:
      runCards.active ||
      captureFor !== null ||
      family !== null ||
      confirmFlow.dialogProps !== null ||
      arrivalDialog !== null ||
      interruptDialog !== null ||
      deskNoteFor !== null ||
      addServiceFor !== null,
  });
  offerOwnerNotesRef.current = ownerNotes.offer;
  const offerOwnerNotes = ownerNotes.offer;

  // Search-first: filter by dog (then owner) name, dog matches ranked
  // first (#547 bias). No query → the full expected-arrivals list, in
  // its server order.
  const visibleArrivals = useMemo(() => {
    const q = query.trim();
    const ranked = arrivals
      .map((item) => ({ item, rank: matchRank(item, q) }))
      .filter((r) => r.rank > 0);
    if (q.length > 0) ranked.sort((a, b) => b.rank - a.rank);
    return ranked.map((r) => r.item);
  }, [arrivals, query]);

  const setLoading = useCallback((bookingIds: ReadonlyArray<string>, on: boolean) => {
    setActionLoading((prev) => {
      const next = new Set(prev);
      for (const id of bookingIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  // Ted #22 (#983) — reopen a checkout from the Outstanding strip. Ted:
  // "There should be a clear way to locate and reopen a checkout if staff
  // needs to make a correction." No optimistic flip: this one voids an
  // invoice, so the strip reconciles from /today rather than guessing.
  const handleReopenCheckout = useCallback(
    async (bookingId: string, reason: string) => {
      if (!token) return;
      setLoading([bookingId], true);
      setActionError(null);
      try {
        await undoCheckOutBooking(apiUrl, token, bookingId, reason.trim());
        setReopening(null);
        setReopenReason('');
        bumpRefresh();
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'Could not reopen that checkout.',
        );
      } finally {
        setLoading([bookingId], false);
      }
    },
    [apiUrl, token, setLoading, bumpRefresh],
  );

  // Sawyer, 10-30 — Check Out on "Going home today": the same visit row, and
  // the same gates, as the Check out link on the board below.
  const goToCheckOut = useCallback(
    (bookingId: string) => navigate(checkOutPath(bookingId)),
    [navigate],
  );

  // Ted #16 — undo a check-in made by mistake. Mirrors handleCheckIn's
  // optimistic shape: the row flips back at once, then /today refetches so
  // the count and list reconcile from source.
  const handleUndoCheckIn = useCallback(
    async (bookingId: string) => {
      if (!token) return;
      setLoading([bookingId], true);
      setActionError(null);
      try {
        await undoCheckInBooking(apiUrl, token, bookingId);
        setCheckedIn((prev) => {
          const next = new Set(prev);
          next.delete(bookingId);
          return next;
        });
        bumpRefresh();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not undo the check-in — try again';
        setActionError(message);
        setTimeout(() => setActionError(null), 5000);
      } finally {
        setLoading([bookingId], false);
      }
    },
    [apiUrl, token, bumpRefresh, setLoading],
  );

  // Ted #2 (#905) — check in every dog of one household with one tap. One
  // POST per booking (the same route as the single button), ALL settled
  // before anything else happens, so Skipper being already checked in never
  // stops Hazel and Hank. Only the ids the server actually accepted flip to
  // "Checked in"; the banner names exactly who did not go through; then ONE
  // refetch reconciles the list from source.
  //
  // #716 — the capture sheet is deliberately NOT offered after a household
  // check-in. The sheet is per booking, so a family of three would open
  // three sheets (modal-on-modal — an anti-pattern), and the ordering
  // argument (check-in first, capture after) already holds: every dog is
  // in before this returns. Capture stays reachable per row when it ships.
  const handleCheckInHousehold = useCallback(
    async (
      rows: DashboardArrival[],
      decisions?: ReadonlyMap<string, InterruptDecision>,
      /** Lane LC — the dogs the step stopped, named on the same banner as the batch's refusals. */
      stoppedNote: string | null = null,
    ) => {
      if (!token) return;
      // Lane LC — the household's UNION of check-in alerts, once, before any
      // POST. A Do Not Admit dog is named and left out; the siblings go in.
      if (!decisions) {
        const plan = planCheckInInterrupt(rows);
        if (plan.needed) {
          askInterrupt({
            key: `household:${rows[0]?.customerId ?? rows.map((r) => r.bookingId).join(',')}`,
            plan,
            onProceed: (result) => {
              const stopped = describeStopped(result.stopped);
              const map = new Map(result.proceed.map((d) => [d.bookingId, d] as const));
              const go = rows.filter((r) => map.has(r.bookingId));
              if (go.length > 0) {
                void handleCheckInHousehold(go, map, stopped);
              } else if (stopped) {
                setActionError(stopped);
                setTimeout(() => setActionError(null), 8000);
              }
            },
          });
          return;
        }
      }
      const ids = rows.map((r) => r.bookingId);
      setLoading(ids, true);
      setActionError(null);
      const outcome = await checkInHousehold(rows, (row) => {
        const d = decisions?.get(row.bookingId);
        return checkInBooking(apiUrl, token, row.bookingId, undefined, d?.arrivalVerified, d?.heatConfirmed);
      });
      if (outcome.succeeded.length > 0) {
        setCheckedIn((prev) => {
          const next = new Set(prev);
          for (const row of outcome.succeeded) next.add(row.bookingId);
          return next;
        });
        // Ted Item 34 — one prompt for the whole family's cards.
        offerRunCards(outcome.succeeded.map((r) => r.bookingId));
        // Sawyer #1296 — ONE owner's-notes question for the family's notes.
        offerOwnerNotes(outcome.succeeded.map(factsFromArrival));
      }
      // Ted #28 (#1050) — THE BATCH RULE. "Check in all" never sweeps a
      // transported dog in: its walk-in siblings are already checked in
      // above, and each transported dog now gets its own question, in turn.
      for (const row of outcome.needsArrivalCheck) {
        askArrival({
          bookingId: row.bookingId,
          dogNames: row.dogNames,
          onConfirm: () => checkInRef.current?.(row.bookingId, true),
        });
      }
      const message = [stoppedNote, summarizeHouseholdCheckIn(outcome)].filter(Boolean).join(' ');
      if (message) {
        setActionError(message);
        setTimeout(() => setActionError(null), stoppedNote ? 8000 : 5000);
      }
      setLoading(ids, false);
      // One refetch for the whole household — even when a dog was refused,
      // the refusal ("already checked in") means the list was stale.
      bumpRefresh();
    },
    [apiUrl, token, bumpRefresh, setLoading, askArrival, askInterrupt, offerRunCards, offerOwnerNotes],
  );

  const handleCheckIn = useCallback(
    async (bookingId: string, arrivalVerified?: boolean, opts: CheckInOpts = {}) => {
      if (!token) return;
      // Lane LC — "Before you check in". Asked from the row's flags BEFORE
      // the POST, unless this call already carries the desk's answers (the
      // step's own re-run, or the 409 fallback's arrival confirmation).
      if (!opts.interrupted && !arrivalVerified) {
        const item = arrivalsRef.current.find((a) => a.bookingId === bookingId);
        const plan = planCheckInInterrupt(item ? [item] : []);
        if (plan.needed) {
          askInterrupt({
            key: bookingId,
            plan,
            onProceed: (result) => {
              const stopped = describeStopped(result.stopped);
              if (stopped) {
                setActionError(stopped);
                setTimeout(() => setActionError(null), 8000);
              }
              for (const d of result.proceed) {
                void checkInRef.current?.(d.bookingId, d.arrivalVerified, {
                  heatConfirmed: d.heatConfirmed,
                  interrupted: true,
                });
              }
            },
          });
          return;
        }
      }
      setLoading([bookingId], true);
      setActionError(null);
      try {
        await checkInBooking(apiUrl, token, bookingId, undefined, arrivalVerified, opts.heatConfirmed);
        // Optimistic: mark done immediately so the row confirms in <100ms,
        // then refetch /today so the count and list reconcile from source.
        setCheckedIn((prev) => new Set(prev).add(bookingId));
        bumpRefresh();
        // #716 — the capture sheet opens AFTER the check-in has already
        // succeeded, never before. That ordering is the whole safety
        // argument: skipping the sheet, losing the network, or closing the
        // tab all leave the dog correctly checked in. Data entry can never
        // block a check-in because by the time it is offered, the check-in
        // is done.
        //
        // Gated three ways, all of which must hold:
        //   1. `features.checkInCapture` — off for every tenant today, so
        //      this page is byte-identical to its pre-#716 behaviour.
        //   2. boarding only — a daycare drop-off stays a one-tap flow.
        //   3. the enriched `dogs` payload is present — an older server
        //      omits dog ids, and the snapshot is per (booking, DOG).
        const item = arrivalsRef.current.find((a) => a.bookingId === bookingId);
        // Sawyer #1296 — asked after the check-in landed; it waits behind
        // the run-card prompt / capture sheet below, never on top of them.
        if (item) offerOwnerNotes([factsFromArrival(item)]);
        const dogs = item?.dogs ?? [];
        if (
          captureEnabled &&
          item &&
          isBoardingServiceName(item.serviceType) &&
          dogs.length > 0
        ) {
          // Ted Item 34 — the run-card prompt waits for the sheet to close
          // (see its onClose), so the card prints what was just captured.
          setCaptureFor({
            bookingId,
            dogs: dogs.map((d) => ({ dogId: d.dogId, name: d.name })),
          });
        } else {
          offerRunCards([bookingId]);
        }
      } catch (err) {
        // Ted #28 (#1050) — not an error the desk did anything wrong about:
        // the dog arrives by transport and nobody has said it is here yet.
        // Ask, then re-run with the answer. The row stays on "Check In"
        // until then — the honest state.
        if (isArrivalUnverified(err)) {
          const item = arrivalsRef.current.find((a) => a.bookingId === bookingId);
          askArrival({
            bookingId,
            dogNames: item?.dogNames ?? [],
            onConfirm: () => checkInRef.current?.(bookingId, true),
          });
          return;
        }
        const message =
          err instanceof Error ? err.message : 'Could not check in — try again';
        setActionError(message);
        setTimeout(() => setActionError(null), 5000);
      } finally {
        setLoading([bookingId], false);
      }
    },
    [apiUrl, token, bumpRefresh, captureEnabled, setLoading, askArrival, askInterrupt, offerRunCards, offerOwnerNotes],
  );
  checkInRef.current = handleCheckIn;

  // Ted #32c (#1160) — Check In on a row. A dog whose family has others still
  // to arrive asks first, with ONLY this dog going in and the siblings
  // unticked; a dog with no sibling checks in with one tap, exactly as before.
  const requestCheckIn = useCallback(
    (bookingId: string) => {
      const row = arrivalsRef.current.find((a) => a.bookingId === bookingId);
      const siblings = row ? siblingsStillToArrive(row, arrivalsRef.current, checkedIn) : [];
      if (!row || siblings.length === 0) {
        void handleCheckIn(bookingId);
        return;
      }
      setFamily({ anchor: row, siblings, startTicked: 'anchor-only' });
    },
    [checkedIn, handleCheckIn],
  );

  // Ted #32c (#1160) — "Check in all (N)" no longer posts without asking: the
  // same picker, with the family ticked because the desk asked for all of them
  // — and a dog that is not here yet one tap from unticked.
  const requestCheckInAll = useCallback((rows: DashboardArrival[]) => {
    if (rows.length === 0) return;
    setFamily({ anchor: null, siblings: rows, startTicked: 'all' });
  }, []);

  // The picker's answer: one id is a plain check-in, several are the batch.
  const confirmFamily = useCallback(
    (ids: readonly string[]) => {
      const picked = family ? [family.anchor, ...family.siblings].filter((r): r is DashboardArrival => r !== null) : [];
      setFamily(null);
      const rows = ids
        .map((id) => picked.find((r) => r.bookingId === id))
        .filter((r): r is DashboardArrival => r !== undefined);
      if (rows.length === 0) return;
      if (rows.length === 1) void handleCheckIn(rows[0]!.bookingId);
      else void handleCheckInHousehold(rows);
    },
    [family, handleCheckIn, handleCheckInHousehold],
  );

  // Ted #2 (#905) — same-owner rows sit together. Computed over the
  // SEARCH-FILTERED list, so typing "Royer" groups the family and typing
  // "Hazel" shows one plain row.
  const householdSegments = useMemo(
    () => segmentByHousehold(visibleArrivals),
    [visibleArrivals],
  );

  // Ted #23 (#984), 09-16 re-review — the headline counts VISITS EXPECTED, not
  // booking rows. A service leg IS a booking row on `/today`, so `.length`
  // read Milo-with-a-nail-trim-and-a-bath as "Expected today 3". The rows
  // themselves stay (and now say what they are); the NUMBER is visits.
  const expectedCount = useMemo(() => countVisits(visibleArrivals), [visibleArrivals]);
  // …and "Going home" counts visits leaving, for the same reason.
  const goingHomeCount = useMemo(() => countVisits(departures), [departures]);

  if (!token) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Front Desk</h1>
        <p className="ferro-muted">Please sign in to continue.</p>
      </section>
    );
  }

  const showHonestEmpty = scheduleSuspect;

  return (
    <section className="ferro-frontdesk" data-testid="front-desk">
      {/* Sawyer 11 client-notes-agreements (F1) — one signals read for the day's rows. */}
      <ClientNoteSignalsProvider apiUrl={apiUrl} customerIds={[...arrivals, ...departures].map((r) => r.customerId)} refreshKey={refreshKey}>
      <header className="ferro-frontdesk__header">
        <div>
          <h1 className="ferro-frontdesk__title">Front Desk</h1>
          <p className="ferro-frontdesk__subtitle">
            {new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <div className="ferro-frontdesk__header-actions">
          {/* #564 — print the morning board (dogs expected + going home).
              Disabled until today's data has loaded, so we never print a
              blank sheet. */}
          <button
            type="button"
            className="ferro-frontdesk__board-btn"
            onClick={() => setBoardOpen(true)}
            disabled={!today.data}
            data-testid="front-desk-print-board"
          >
            <Icon name="print" size={22} />
            <span>Print morning board</span>
          </button>
          {/* Sawyer answers 10-29, item 2 — the desk's checkout list (Gingr's
              Check Out Report): who goes home today, with belongings, locker
              and food to send home. Same /today data as the list below. */}
          <button
            type="button"
            className="ferro-frontdesk__board-btn"
            onClick={() => setGoingHomeOpen(true)}
            disabled={!today.data}
            data-testid="front-desk-print-going-home"
          >
            <Icon name="print" size={22} />
            <span>Print going home</span>
          </button>
          {/* Ted Item 34 — today's run cards, from where the desk works
              (Ted could not find them on Bookings). Every boarder on site
              today, as a preview with a Print button. */}
          <button
            type="button"
            className="ferro-frontdesk__board-btn"
            onClick={runCards.printToday}
            disabled={runCards.loadingToday}
            data-testid="front-desk-print-run-cards"
          >
            <Icon name="print" size={22} />
            <span>{runCards.loadingToday ? 'Loading run cards…' : 'Print run cards'}</span>
          </button>
          {/* Clock in/out shortcut — links to the existing Time Clock
              surface; this page never reimplements punches. */}
          <Link
            to="/timeclock"
            className="ferro-frontdesk__clock"
            data-testid="front-desk-clock-link"
          >
            <Icon name="timeclock" size={22} />
            <span>Clock in / out</span>
          </Link>
        </div>
      </header>

      {/* Principle 5 — the server's honesty warning, verbatim, never
          swallowed. A broken sync must not read as a quiet day. */}
      {dataStatus.data?.warning ? (
        <div
          className="ferro-frontdesk__banner"
          role="status"
          data-testid="front-desk-data-banner"
        >
          {dataStatus.data.warning}
        </div>
      ) : null}

      {/* Search-first check-in ------------------------------------- */}
      <div className="ferro-frontdesk__search">
        <Icon name="search" size={24} className="ferro-frontdesk__search-icon" />
        <input
          type="search"
          className="ferro-frontdesk__search-input"
          placeholder="Type a dog's name to check in…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search today's dogs by name to check in"
        />
        {query ? (
          <button
            type="button"
            className="ferro-frontdesk__search-clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            <Icon name="close" size={20} />
          </button>
        ) : null}
      </div>

      {actionError ? (
        <div className="ferro-frontdesk__error" role="alert">
          {actionError}
        </div>
      ) : null}

      {/* Ted #26 — what the confirm actually did, on the page (a toast is not
        * a record of five status changes). Tone and aria-role come from the
        * outcome itself (`ConfirmOutcomeBanner`): a refusal or a failure is
        * never painted green, and "nothing was confirmed" is announced. */}
      {confirmFlow.banner ? (
        <div
          className={`ferro-banner ferro-banner--${confirmFlow.banner.tone}`}
          role={confirmFlow.banner.role}
          data-testid="front-desk-confirm-banner"
          data-tone={confirmFlow.banner.tone}
        >
          <p>{confirmFlow.banner.text}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={confirmFlow.dismissBanner}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Ted #32c (#1158) — what a Cancel / Edit / No-show just did, on the
          page (a toast is not a record). Tone and role come from the outcome:
          a refusal is never painted green. */}
      {reservation.banner}

      {/* Ted #22 (#983) — "Juniper then disappeared from the checkout area,
          and I could not readily determine where the transaction went."
          Every checkout today that is still unpaid, at the top of the desk's
          own screen, with the two things staff want: reopen it, or settle /
          adjust it. Renders only when there is something outstanding — a
          calm morning shows nothing at all (Principle 1). */}
      {outstanding.length > 0 ? (
        <section
          className="ferro-frontdesk__outstanding"
          aria-label="Checkouts still unpaid today"
          data-testid="front-desk-outstanding"
        >
          <h2 className="ferro-frontdesk__section-title">
            Outstanding today
            <span className="ferro-frontdesk__count">{outstanding.length}</span>
          </h2>
          <ul className="ferro-frontdesk__outstanding-list">
            {outstanding.map((row) => (
              <li
                key={row.bookingId}
                className="ferro-frontdesk__outstanding-row"
                data-testid={`front-desk-outstanding-${row.bookingId}`}
              >
                <div className="ferro-frontdesk__outstanding-who">
                  <span className="ferro-frontdesk__outstanding-dogs">
                    {row.dogNames.length > 0 ? row.dogNames.join(', ') : 'Booking'}
                  </span>
                  <span className="ferro-frontdesk__outstanding-meta">
                    <OwnerLink name={row.customerName} customerId={row.customerId} /> · {row.serviceDisplayName ?? row.serviceType} ·
                    Checkout started{' '}
                    {row.checkedOutAt ? formatTime(row.checkedOutAt) : 'today'} ·{' '}
                    {/* A $0 amount is the Juniper case — say "unpaid", never
                        let a zero read as "nothing owed, nothing to do". */}
                    {row.amountDueCents > 0
                      ? `$${(row.amountDueCents / 100).toFixed(2)} unpaid`
                      : 'unpaid — no price yet'}
                  </span>
                </div>
                {reopening === row.bookingId ? (
                  <div className="ferro-frontdesk__outstanding-reason">
                    <label className="ferro-field">
                      <span className="ferro-field__label">Why? (required)</span>
                      <input
                        type="text"
                        className="ferro-field__input"
                        value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                        placeholder="e.g. missed the multi-dog discount"
                        maxLength={200}
                        data-testid={`front-desk-reopen-reason-${row.bookingId}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--primary"
                      disabled={
                        reopenReason.trim().length < 2 ||
                        actionLoading.has(row.bookingId)
                      }
                      onClick={() =>
                        void handleReopenCheckout(row.bookingId, reopenReason)
                      }
                      data-testid={`front-desk-reopen-confirm-${row.bookingId}`}
                    >
                      Reopen checkout
                    </button>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => {
                        setReopening(null);
                        setReopenReason('');
                      }}
                      data-testid={`front-desk-reopen-cancel-${row.bookingId}`}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="ferro-frontdesk__outstanding-actions">
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => {
                        setReopenReason('');
                        setReopening(row.bookingId);
                      }}
                      data-testid={`front-desk-reopen-${row.bookingId}`}
                    >
                      Reopen checkout
                    </button>
                    <Link
                      className="ferro-btn ferro-btn--primary"
                      to="/payments"
                      data-testid={`front-desk-settle-${row.bookingId}`}
                    >
                      Settle / adjust
                    </Link>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {confirmFlow.dialogProps ? (
        <ConfirmPendingDialog {...confirmFlow.dialogProps} />
      ) : null}
      {/* Ted #28 (#1050) — "Is Bella here?" for a transported arrival. */}
      {arrivalDialog}
      {/* Lane LC — "Before you check in" (flags, Do Not Admit stop, heat question). */}
      {interruptDialog}
      {/* Ted #32c (#1158) — the Cancel / Edit / No-show dialog, one at a time. */}
      {reservation.dialogs}
      {/* Ted #32c (#1160) — "who else is here?", siblings unticked by default. */}
      {family ? (
        <FamilyCheckInDialog
          key={family.anchor?.bookingId ?? 'all'}
          anchor={family.anchor ? familyMemberFromArrival(family.anchor) : null}
          siblings={family.siblings.map(familyMemberFromArrival)}
          ownerLabel={(family.anchor ?? family.siblings[0])?.customerName ?? 'this family'}
          startTicked={family.startTicked}
          onClose={() => setFamily(null)}
          onConfirm={confirmFamily}
        />
      ) : null}

      <h2 className="ferro-frontdesk__section-title">
        {query.trim() ? 'Matching dogs' : 'Expected today'}
        {today.data ? (
          <span className="ferro-frontdesk__count" data-testid="front-desk-expected-count">
            {expectedCount}
          </span>
        ) : null}
      </h2>

      {/* Loading (skeleton, never a blank screen) */}
      {!today.data && today.isLoading ? (
        <div className="ferro-frontdesk__skeleton" data-testid="front-desk-skeleton">
          <div className="ferro-frontdesk__skeleton-row" />
          <div className="ferro-frontdesk__skeleton-row" />
          <div className="ferro-frontdesk__skeleton-row" />
        </div>
      ) : null}

      {/* Error (what happened + how to recover) */}
      {today.error ? (
        <div className="ferro-frontdesk__error" role="alert">
          <span>Couldn&apos;t load today&apos;s dogs.</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={today.retry}
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Arrivals list — dog-first rows, one-tap Check In */}
      {today.data ? (
        <ul className="ferro-frontdesk__list">
          {visibleArrivals.length === 0 ? (
            <li className="ferro-frontdesk__empty" data-testid="front-desk-empty">
              {query.trim()
                ? 'No dogs match that name.'
                : showHonestEmpty
                  ? "Today's schedule isn't in this system yet."
                  : 'No dogs expected today.'}
            </li>
          ) : null}
          {householdSegments.map((segment) => {
            const renderRow = (item: DashboardArrival): JSX.Element => (
              <FrontDeskRow
                key={item.bookingId}
                item={item}
                done={checkedIn.has(item.bookingId)}
                loading={
                  actionLoading.has(item.bookingId) ||
                  confirmFlow.busyIds.has(item.bookingId) ||
                  reservation.busyIds.has(item.bookingId)
                }
                awaitingConfirmation={item.status === 'pending'}
                onConfirm={() => beginConfirm(item)}
                onCheckIn={() => requestCheckIn(item.bookingId)}
                onUndo={() => void handleUndoCheckIn(item.bookingId)}
                onAddService={() => setAddServiceFor(item)}
                onDogClick={openDogCard}
                onEdit={() => reservation.edit(reservationTargetFromArrival(item, date))}
                onNoShow={() => reservation.noShow(reservationTargetFromArrival(item, date))}
                onCancel={() => reservation.cancel(reservationTargetFromArrival(item, date))}
                onDeskNote={() => setDeskNoteFor(item)}
              />
            );
            // #984 09-16 re-review — a household is a family of VISITS. A leg
            // carries its visit's owner AND dog, so counting it as a member
            // made one dog with a nail trim a "Milo & Milo" card, and titled a
            // real family "Hazel, Hazel & Hank". The leg still renders inside
            // the card; it just never makes, names or widens one.
            const visits = segment.rows.filter((r) => r.isLeg !== true);
            if (segment.customerId === null || visits.length < 2) {
              return segment.rows.map(renderRow);
            }
            // Ted #2 (#905) — the household card. The bulk button counts
            // only the dogs still to check in and disappears below two
            // (the per-row buttons remain for the last one).
            // Legs are excluded from the bulk action AND its count: the
            // parent's check-in already cascades to them (booking/service.ts
            // rule 3), so counting them would promise the desk more dogs than
            // there are and calling them would be N redundant requests.
            const pending = visits.filter((r) => !checkedIn.has(r.bookingId));
            const dogs = joinDogNames(householdDogNames(visits));
            const busy = segment.rows.some((r) => actionLoading.has(r.bookingId));
            return (
              <li
                key={`household-${segment.customerId}`}
                className="ferro-frontdesk__household"
                data-testid={`front-desk-household-${segment.customerId}`}
              >
                <div className="ferro-frontdesk__household-head">
                  <p className="ferro-frontdesk__household-title">
                    <span className="ferro-frontdesk__household-dogs">{dogs}</span>
                    <span className="ferro-frontdesk__household-owner">
                      {' — '}
                      <OwnerLink name={segment.customerName} customerId={segment.customerId} />
                    </span>
                  </p>
                  {pending.length >= 2 ? (
                    <button
                      type="button"
                      className="ferro-frontdesk__checkin ferro-frontdesk__checkin--household"
                      onClick={() => requestCheckInAll(pending)}
                      disabled={busy}
                      data-testid={`front-desk-check-in-household-${segment.customerId}`}
                      aria-label={`Check in all ${pending.length}: ${joinDogNames(householdDogNames(pending))} — ${segment.customerName}`}
                    >
                      {busy ? '…' : `Check in all (${pending.length})`}
                    </button>
                  ) : null}
                </div>
                <ul className="ferro-frontdesk__household-rows">
                  {segment.rows.map(renderRow)}
                </ul>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Sawyer answers 10-29, item 2 — "Going home today": every dog with a
          checkout today, by pick-up time, with what it brought, its locker
          and whether food goes home. Its Check Out opens the Bookings money
          path, the same as the board below; the list posts nothing. */}
      <GoingHomeToday
        data={today.data}
        isLoading={today.isLoading}
        error={today.error}
        onRetry={today.retry}
        dataSuspect={scheduleSuspect}
        onCheckOut={goToCheckOut}
      />

      {/* Checkout — the board stays EXCLUDED BY DEFAULT (see file header).
          A toggle reveals departures; checkout routes to the Bookings
          money-path orchestrator, never a direct POST. */}
      <div className="ferro-frontdesk__checkout-toggle">
        <button
          type="button"
          className="ferro-frontdesk__toggle-btn"
          onClick={() => setShowCheckout((v) => !v)}
          aria-expanded={showCheckout}
          data-testid="front-desk-checkout-toggle"
        >
          {showCheckout ? 'Hide checkout' : 'Show checkout'}
        </button>
      </div>

      {showCheckout ? (
        <>
          <h2 className="ferro-frontdesk__section-title">
            Going home
            {today.data ? (
              <span
                className="ferro-frontdesk__count"
                data-testid="front-desk-departures-count"
              >
                {goingHomeCount}
              </span>
            ) : null}
          </h2>
          <ul className="ferro-frontdesk__list" data-testid="front-desk-departures">
            {departures.length === 0 ? (
              <li className="ferro-frontdesk__empty">
                {showHonestEmpty
                  ? "Today's schedule isn't in this system yet."
                  : 'No dogs going home today.'}
              </li>
            ) : null}
            {departures.map((item) => (
              <li
                key={item.bookingId}
                className="ferro-frontdesk__row"
                data-testid={`front-desk-departure-${item.bookingId}`}
              >
                <div className="ferro-frontdesk__row-body">
                  <FrontDeskDogNames
                    dogs={item.dogs}
                    dogNames={item.dogNames}
                    onDogClick={openDogCard}
                    unpaid={item.unpaid}
                    balanceDueCents={item.balanceDueCents}
                    storeCreditCents={item.walletBalanceCents}
                  />
                  <p className="ferro-frontdesk__meta">
                    <OwnerLink name={item.customerName} customerId={item.customerId} /> <FrontDeskOwnerFlags item={item} /> · {item.isLeg ? (item.addOnName ?? item.serviceType) : item.serviceType}
                    {item.expectedTime ? ` · ${formatTime(item.expectedTime)}` : ''}
                  </p>
                  {/* Sawyer 03 today-notes-board (C2, G8; refuter C-1) — a
                      checked-in daycare dog lives here for the rest of the day,
                      so its notes do too. */}
                  {item.isLeg ? null : (
                    <BookingNoteCallout
                      bookingNote={item.bookingNote}
                      deskNotes={deskNotesOf(item)}
                      testIdPrefix={`front-desk-departure-note-${item.bookingId}`}
                    />
                  )}
                  <DeskNoteButton
                    item={item}
                    disabled={false}
                    onClick={() => setDeskNoteFor(item)}
                    testId={`front-desk-departure-desk-note-${item.bookingId}`}
                  />
                  {item.isLeg ? (
                    <p
                      className="ferro-frontdesk__leg-tag"
                      data-testid={`front-desk-departure-leg-${item.bookingId}`}
                    >
                      Added service on this visit
                    </p>
                  ) : null}
                  {/* Sawyer 10-30 — a checked-in dog's card by hand. "Print run
                      cards" above is the boarders' deck; with daycare off the
                      check-in prompt, this is the way to a daycare dog's card. */}
                  {item.isLeg ? null : (
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-booking-note__add"
                      onClick={() => runCards.printCards([item.bookingId])}
                      disabled={runCards.active}
                      data-testid={`front-desk-run-card-${item.bookingId}`}
                      aria-label={`Print the run card for ${item.dogNames.join(', ') || item.customerName}`}
                    >
                      Run card
                    </button>
                  )}
                  {/* Sawyer F1 — client notes (lane 11) */}
                  <ClientNotesRowSlot customerId={item.customerId} rowKey={item.bookingId} />
                </div>
                {/* Ted #23 (#984) 09-16 re-review — a leg leaves WITH its visit:
                    the visit's check-out cascades to it and bills it on the one
                    itemized checkout. A Check out link here opened the money
                    path for the nail trim alone, leaving the visit's invoice
                    behind. Same treatment as the arrival row's leg. */}
                {item.isLeg ? (
                  <span
                    className="ferro-frontdesk__leg-rides"
                    data-testid={`front-desk-departure-leg-rides-${item.bookingId}`}
                  >
                    Checks out with the visit
                  </span>
                ) : (
                  <Link
                    to={checkOutPath(item.bookingId)}
                    className="ferro-frontdesk__checkout-link"
                  >
                    Check out
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* #564 — the printable morning board (portals to <body>). Uses the
          SAME /today data as the list above, so the paper board can never
          disagree with the screen (Principle 1). */}
      {boardOpen ? (
        <ArrivalsBoard
          facilityName={config.brand.name}
          date={date}
          arrivals={arrivals}
          departures={departures}
          dataSuspect={scheduleSuspect}
          warning={dataStatus.data?.warning ?? null}
          onClose={() => setBoardOpen(false)}
          // Sawyer 03 today-notes-board (A4) — mid-stay boarders (absent on an older server).
          inHouse={today.data?.onSite}
        />
      ) : null}

      {goingHomeOpen ? (
        <GoingHomeBoard
          facilityName={config.brand.name}
          date={date}
          arrivals={arrivals}
          departures={departures}
          dataSuspect={scheduleSuspect}
          onClose={() => setGoingHomeOpen(false)}
        />
      ) : null}

      {/* #716 — the check-in capture sheet. The dog is ALREADY checked in
          when this renders; closing it (Skip, Esc, overlay) is always a
          safe exit. Keyed on the booking so re-checking a different dog
          gets a fresh sheet rather than a stale one. */}
      {captureFor ? (
        <CheckInCareSheet
          key={captureFor.bookingId}
          apiUrl={apiUrl}
          token={token}
          bookingId={captureFor.bookingId}
          dogs={captureFor.dogs}
          onClose={() => {
            const done = captureFor.bookingId;
            setCaptureFor(null);
            offerRunCards([done]);
          }}
        />
      ) : null}

      {runCards.element}
      {ownerNotes.element}

      {/* Ted #23 (#984) — "Add a service" from the Front Desk row. */}
      {addServiceFor ? (
        <AddServiceDialog
          apiUrl={apiUrl}
          token={token ?? ''}
          bookingId={addServiceFor.bookingId}
          bookingLabel={`${addServiceFor.dogNames.join(', ')} — ${addServiceFor.customerName}`}
          parentCheckedIn={checkedIn.has(addServiceFor.bookingId)}
          onClose={() => setAddServiceFor(null)}
          onAdded={bumpRefresh}
        />
      ) : null}

      {/* Sawyer 03 today-notes-board (G8) — the desk's same-day note, saved
          through the existing #1218 run-card-edits PUT. */}
      {deskNoteFor && token ? (
        <DeskNoteDialog
          key={deskNoteFor.bookingId}
          apiUrl={apiUrl}
          token={token}
          bookingId={deskNoteFor.bookingId}
          dogs={(deskNoteFor.dogs ?? []).map((d) => ({ dogId: d.dogId, name: d.name }))}
          current={deskNotesOf(deskNoteFor) ?? []}
          onSaved={bumpRefresh}
          onClose={() => setDeskNoteFor(null)}
        />
      ) : null}

      {/* #581-followup — pet ID card, opened by clicking a dog's name above. */}
      {openDog ? (
        <DogIdCard
          apiUrl={apiUrl}
          dogId={openDog.id}
          fallbackName={openDog.name}
          onClose={() => setOpenDog(null)}
        />
      ) : null}
      </ClientNoteSignalsProvider>
    </section>
  );
}

/**
 * #581-followup — render the row's dog name(s) as the hero, each name a button
 * that opens the pet ID card ("if you click on the name of the dog… the pet
 * card"). Uses the enriched `item.dogs` (carries dog ids) when present; falls
 * back to the plain `dogNames` string for stale servers that omit `dogs`
 * (deploy-order safety) — no reshaping of the payload, just using the id-
 * bearing field when it's there.
 *
 * #678 — each dog's name is followed by the SAME `DogStatusStrip` the
 * dashboard, Bookings, and the print board render (syringe ✕ expired /
 * ! expiring / ? none; current = deliberately NO badge). The receptionist's
 * quick-card teaches vax triage AT CHECK-IN, so the badge must exist on the
 * surface she performs check-ins from. Reusing the shared strip (never a
 * fork) keeps the semantics single-sourced — this page can never disagree
 * with the card or the other surfaces (Principle 1: one glance, one truth).
 * Mirrors DogFirstCell's composition exactly: the row's balance-due state
 * renders on the LEAD dog's strip only; every additional dog carries its own
 * bare strip. The fallback branch (no enriched `dogs`) stays strip-less —
 * we never invent a status we don't have (Principle 5: never lie about data).
 */
function FrontDeskDogNames({
  dogs,
  dogNames,
  onDogClick,
  unpaid,
  balanceDueCents,
  storeCreditCents,
  hasMedications,
}: {
  dogs: DogCard[] | undefined;
  dogNames: string[];
  onDogClick: (id: string, name: string) => void;
  /**
   * #678 / Ted #7 (#910) / #717 — the row's LEGACY facts. Ted #28 Phase 2
   * (#1050) renders every one of them from the registry's flags; these feed
   * the deploy-window shim only (`flag-skew.ts`) and go once the Phase 2
   * server is live. The meds flag is per DOG now (which dog has meds), not
   * one MEDS chip for the booking.
   */
  unpaid?: boolean;
  balanceDueCents?: number;
  storeCreditCents?: number;
  hasMedications?: boolean;
}): JSX.Element {
  if (Array.isArray(dogs) && dogs.length > 0) {
    return (
      <p className="ferro-frontdesk__dog">
        {dogs.map((d, i) => (
          <span key={d.dogId}>
            {i > 0 ? ', ' : ''}
            <button
              type="button"
              className="ferro-frontdesk__dogname-btn"
              onClick={() => onDogClick(d.dogId, d.name)}
              aria-haspopup="dialog"
              aria-label={`Open ${d.name}'s pet card`}
              data-testid={`front-desk-dog-name-${d.dogId}`}
            >
              {d.name}
            </button>
            <DogStatusStrip
              dog={d}
              legacy={i === 0 ? { unpaid, balanceDueCents, storeCreditCents, hasMedications } : undefined}
            />
          </span>
        ))}
      </p>
    );
  }
  // The fallback branch (no enriched `dogs`) stays strip-less — we never
  // invent a status we don't have (Principle 5).
  return <p className="ferro-frontdesk__dog">{dogNames.join(', ')}</p>;
}

/**
 * Ted #28 Phase 2 (#1050) — the OWNER's flags (`$` balance, store credit,
 * unsigned agreement) drawn right after the owner's name on the row's meta
 * line — Ted: money "follows underneath the client's name". From the lead
 * dog's list (every dog of an owner carries the same account flags).
 */
function FrontDeskOwnerFlags({ item }: { item: DashboardArrival }): JSX.Element | null {
  const lead = item.dogs?.[0];
  // Sawyer 11 client-notes-agreements (F1) — the client-note chip follows the
  // owner's flags, and shows even on a row without a lead dog.
  const noteChip = item.customerId ? (
    <ClientNoteChip variant="toggle" customerId={item.customerId} rowKey={item.bookingId} />
  ) : null;
  if (!lead) return noteChip;
  return (
    <>
    <DogFlagChips
      flags={resolveDogFlags(lead, {
        unpaid: item.unpaid,
        balanceDueCents: item.balanceDueCents,
        storeCreditCents: item.walletBalanceCents,
      })}
      scope="account"
      testIdPrefix="front-desk-owner-"
    />
    {noteChip}
    </>
  );
}

/**
 * Sawyer 03 today-notes-board (G8) — "Add note" / "Edit note" for the desk's
 * same-day note. Rendered only where the server can take it: never on a leg
 * (the route answers 409 `leg_follows_visit`), never when the row carries no
 * `deskNotes` key (an older server, which may not even have the route), and
 * never without dog ids to address. Front Desk is already gated to the roles
 * the PUT admits (owner, manager, receptionist).
 */
function DeskNoteButton({
  item,
  disabled,
  onClick,
  testId,
}: {
  item: DashboardArrival;
  disabled: boolean;
  onClick: () => void;
  testId: string;
}): JSX.Element | null {
  const notes = deskNotesOf(item);
  if (item.isLeg === true || notes === null || (item.dogs?.length ?? 0) === 0) return null;
  const label = notes.length > 0 ? 'Edit note' : 'Add note';
  return (
    <button
      type="button"
      className="ferro-btn ferro-btn--ghost ferro-booking-note__add"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={`${label} for ${item.dogNames.join(', ')}`}
    >
      {label}
    </button>
  );
}

interface FrontDeskRowProps {
  item: DashboardArrival;
  done: boolean;
  loading: boolean;
  /** Ted #26 — the row is still awaiting confirmation. */
  awaitingConfirmation: boolean;
  onConfirm: () => void;
  onCheckIn: () => void;
  /** Ted #16 — reverse a check-in made by mistake, from the same row. */
  onUndo: () => void;
  /** Ted #23 (#984) — add a service to this visit, before OR after check-in. */
  onAddService: () => void;
  onDogClick: (id: string, name: string) => void;
  /** Ted #32c (#1158) — the Expected row's own reservation actions. */
  onEdit: () => void;
  onNoShow: () => void;
  onCancel: () => void;
  /** Sawyer 03 today-notes-board (G8) — open the desk's same-day note. */
  onDeskNote: () => void;
}

/**
 * This page's OWN lightweight row (NOT the shared ArrivalsDepartures) —
 * keeps #545's restyle of the shared dashboard components off this
 * lane's files. Dog-first: the pet name is the hero, owner + service +
 * time are the supporting line.
 */
function FrontDeskRow({
  item,
  done,
  loading,
  awaitingConfirmation,
  onConfirm,
  onCheckIn,
  onUndo,
  onAddService,
  onDogClick,
  onEdit,
  onNoShow,
  onCancel,
  onDeskNote,
}: FrontDeskRowProps): JSX.Element {
  return (
    <li
      className="ferro-frontdesk__row"
      data-testid={`front-desk-arrival-${item.bookingId}`}
    >
      <div className="ferro-frontdesk__row-body">
        <FrontDeskDogNames
          dogs={item.dogs}
          dogNames={item.dogNames}
          onDogClick={onDogClick}
          unpaid={item.unpaid}
          balanceDueCents={item.balanceDueCents}
          hasMedications={item.hasMedications}
        />
        <p className="ferro-frontdesk__meta">
          {/* Ted #23 (#984) review fix — a SERVICE LEG reads as the service it
              is. Without this the nail trim on Milo's daycare showed here (and
              on the printed arrivals board) as a second untagged arrival row
              saying "Grooming", so one dog was counted as two arrivals — the
              same non-itemization Ted reported, on the busiest desk screen.
              Falls back to the service name when the server is older (absent
              ⇒ an ordinary booking), never to a wrong label. */}
          <OwnerLink name={item.customerName} customerId={item.customerId} /> <FrontDeskOwnerFlags item={item} /> · {item.isLeg ? (item.addOnName ?? item.serviceType) : item.serviceType}
          {item.expectedTime ? ` · ${formatTime(item.expectedTime)}` : ''}
        </p>
        {/* Sawyer 03 today-notes-board (C2, G8) — the owner's note and the
            desk's same-day note, read before Check In. A leg carries none
            (its visit row does). */}
        {item.isLeg ? null : (
          <BookingNoteCallout
            bookingNote={item.bookingNote}
            deskNotes={deskNotesOf(item)}
            testIdPrefix={`front-desk-note-${item.bookingId}`}
          />
        )}
        <DeskNoteButton item={item} disabled={loading} onClick={onDeskNote} testId={`front-desk-desk-note-${item.bookingId}`} />
        {/* Ted 10-09 (#915) — the desk entered this one without telling the
            client: say so before the client walks in asking. */}
        <ClientNotNotifiedPill
          notified={item.customerNotified}
          status={item.status}
          testId={`front-desk-client-not-notified-${item.bookingId}`}
        />
        {/* Sawyer 10 customer-booking-verify-policy (C5 C6) — owner care pills (lane 10) */}
        <OwnerCareCheckPill
          careAttestedAt={item.careAttestedAt}
          medicationPolicyAckAt={item.medicationPolicyAckAt}
          testId={`front-desk-owner-care-${item.bookingId}`}
        />
        {item.isLeg ? (
          <p
            className="ferro-frontdesk__leg-tag"
            data-testid={`front-desk-leg-${item.bookingId}`}
          >
            Added service on this visit
            {item.needsSpecialist ? ' · needs a specialist' : ''}
          </p>
        ) : null}
        {/* Sawyer F1 — client notes (lane 11) */}
        <ClientNotesRowSlot customerId={item.customerId} rowKey={item.bookingId} />
        {/* Ted #32c (#1158) — Gingr's Expected row: ✓ check in, X cancel, and
            the other options edit the reservation. Only while the dog has not
            arrived, and never on a leg (it follows its visit). */}
        {item.isLeg || done ? null : (
          <ExpectedRowActions
            bookingId={item.bookingId}
            dogLabel={item.dogNames.join(', ') || item.customerName}
            testIdPrefix="front-desk"
            disabled={loading}
            onEdit={onEdit}
            onNoShow={onNoShow}
            onCancel={onCancel}
          />
        )}
      </div>
      {/* Ted #23 (#984) — "if a client or staff member adds grooming or
          another add-on AFTER the dog has already been checked in, it may not
          appear anywhere obvious." So the button is on the row in BOTH states,
          not only before check-in.
          NOT on a leg row (09-16 review): the server refuses a leg on a leg
          with 409 `leg_parent_is_a_leg`, so offering it produced an error
          toast and nothing else. Bookings.tsx already gated on `!b.isLeg`;
          this row could not, because DashboardArrival carried no isLeg — the
          fix was the missing field on /today, not a second rule here. */}
      {item.isLeg ? null : (
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-frontdesk__add-service"
          onClick={onAddService}
          disabled={loading}
          data-testid={`front-desk-add-service-${item.bookingId}`}
          aria-label={`Add a service for ${item.dogNames.join(', ')}`}
        >
          Add a service
        </button>
      )}
      {/* Ted #23 (#984) 09-16 re-review — a LEG ROW CARRIES NO LIFECYCLE
          BUTTON OF ITS OWN. The household button already excludes legs
          (`r.isLeg !== true`) precisely because the parent's check-in cascades
          to them (`cascadeToServiceLegs`), so a per-leg Check In made the two
          controls disagree about what a leg is — and the disagreement was the
          dangerous way round: tapping it flipped ONLY the nail trim to
          checked_in and showed a green "Checked in", while Milo's actual
          daycare visit stayed `confirmed`, the dog was never counted on the
          floor, and the desk had visual confirmation of an arrival the system
          did not believe in. Undo had the mirror-image fault. The row says
          what governs it instead of offering a control that lies. */}
      {item.isLeg ? (
        <span
          className="ferro-frontdesk__leg-rides"
          data-testid={`front-desk-leg-rides-${item.bookingId}`}
        >
          {done ? 'Checked in with the visit' : 'Checks in with the visit'}
        </span>
      ) : done ? (
        <span
          className="ferro-frontdesk__done"
          data-testid={`front-desk-done-${item.bookingId}`}
        >
          <Icon name="compliance" size={20} />
          Checked in
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onUndo}
            disabled={loading}
            data-testid={`front-desk-undo-check-in-${item.bookingId}`}
            aria-label={`Undo check-in for ${item.dogNames.join(', ')}`}
          >
            {loading ? '…' : 'Undo'}
          </button>
        </span>
      ) : (
        <span className="ferro-frontdesk__row-actions">
          {/* Ted #26 (#987) — a reservation nobody has confirmed yet says so,
            * and can be confirmed here. The dialog appears only when the
            * household has others pending; otherwise it is one tap. Check In
            * stays available: the desk is never blocked by paperwork when
            * the dog is physically standing there. */}
          {awaitingConfirmation ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onConfirm}
              disabled={loading}
              data-testid={`front-desk-confirm-${item.bookingId}`}
              aria-label={`Confirm the reservation for ${item.dogNames.join(', ')}`}
            >
              {loading ? '…' : 'Confirm'}
            </button>
          ) : null}
          <button
            type="button"
            className="ferro-frontdesk__checkin"
            onClick={onCheckIn}
            disabled={loading}
            data-testid={`front-desk-check-in-${item.bookingId}`}
            aria-label={`Check in ${item.dogNames.join(', ')}`}
          >
            {loading ? '…' : 'Check In'}
          </button>
        </span>
      )}
    </li>
  );
}
